import React, { useEffect, useRef, useState } from "react";
import { gsap } from "../lib/gsap";
import profile from "../data/profile";
import { FaBars, FaTimes } from "react-icons/fa";

const NAV_ITEMS = [
  { label: "Home", idx: 0 },
  { label: "About", idx: 2 },
  { label: "Projects", idx: 3 },
  { label: "Experience", idx: 4 },
  { label: "Education", idx: 5 },
  { label: "Contact", idx: 6 },
];

function getTime() {
  return new Date().toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" });
}

export default function Navbar() {
  const [time, setTime] = useState("");
  const [darkMode, setDarkMode] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const lastY = useRef(0);
  const hidden = useRef(false);
  const stopTimer = useRef(null);

  useEffect(() => {
    setTime(getTime());
    const id = setInterval(() => setTime(getTime()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const scroller = document.querySelector(".main-scroller");
    if (!scroller) return;
    const vh = () => window.innerHeight;
    function show() {
      if (!hidden.current) return;
      gsap.to(headerRef.current, { y: "0%", duration: 0.35, ease: "power2.out" });
      hidden.current = false;
    }
    function onScroll() {
      const y = scroller.scrollTop;
      const delta = y - lastY.current;
      const idx = Math.round(y / vh());
      // Hero (idx 1), Projects (3), Education (5) are light; other sections dark
      setDarkMode(!(idx === 1 || idx === 3 || idx === 5));
      if (delta > 8 && !hidden.current) {
        gsap.to(headerRef.current, { y: "-100%", duration: 0.35, ease: "power2.inOut" });
        hidden.current = true;
      } else if (delta < -6) show();
      lastY.current = y;
      clearTimeout(stopTimer.current);
      stopTimer.current = setTimeout(show, 400);
    }
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => { scroller.removeEventListener("scroll", onScroll); clearTimeout(stopTimer.current); };
  }, []);

  function goTo(idx) {
    if (typeof window !== "undefined" && window.__portfolioGoTo) {
      window.__portfolioGoTo(idx);
    } else {
      const s = document.querySelector(".main-scroller");
      if (s) gsap.to(s, { scrollTop: idx * window.innerHeight, duration: 1.0, ease: "power3.inOut" });
    }
    setMenuOpen(false);
  }

  // The mobile menu overlay is always dark, regardless of which section is
  // behind it, so once it's open the header controls sitting on top of it
  // must stay in their light (paper-on-ink) styling too, not whatever the
  // current section's brightness would otherwise call for.
  const effectiveDark = menuOpen ? true : darkMode;
  const textCol = effectiveDark ? "text-[var(--paper)]" : "text-[var(--ink)]";
  const borderCol = effectiveDark ? "border-[rgba(245,242,236,0.2)]" : "border-[rgba(10,10,11,0.15)]";
  const btnBg = effectiveDark ? "bg-[var(--paper)] text-[var(--ink)]" : "bg-[var(--ink)] text-[var(--paper)]";

  return (
    <>
      <header ref={headerRef} data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-5 md:px-10 py-4 ${textCol} transition-colors`}>
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase hidden md:block">
          United Kingdom · {time}
        </span>

        <nav className="hidden md:flex items-center gap-6">
          {NAV_ITEMS.map(item => (
            <button key={item.label} onClick={() => goTo(item.idx)} data-testid={`nav-${item.label.toLowerCase()}`}
              className="text-xs tracking-[0.2em] uppercase font-medium hover:text-[var(--accent)] transition">
              {item.label}
            </button>
          ))}
        </nav>

        <a href={`mailto:${profile.email}`} data-testid="nav-email-cta"
           className={`hidden md:inline-flex items-center gap-2 rounded-full text-[11px] font-semibold px-4 py-2 ${btnBg} hover:opacity-90 transition`}>
          Email me
        </a>

        <span className="font-mono text-[10px] tracking-[0.25em] uppercase md:hidden font-semibold">AS.</span>
        <button className={`md:hidden w-9 h-9 rounded-full border ${borderCol} flex items-center justify-center`}
          onClick={() => setMenuOpen(o => !o)} data-testid="nav-hamburger" aria-label="Menu">
          {menuOpen ? <FaTimes size={14} /> : <FaBars size={14} />}
        </button>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[99] bg-[var(--ink)] text-[var(--paper)] flex flex-col items-center justify-center gap-6" data-testid="mobile-menu">
          {NAV_ITEMS.map(item => (
            <button key={item.label} onClick={() => goTo(item.idx)}
              className="font-serif text-3xl italic hover:text-[var(--accent)]">
              {item.label}
            </button>
          ))}
          <a href={`mailto:${profile.email}`} className="mt-4 btn-cta">Email me</a>
        </div>
      )}
    </>
  );
}
